import {createSelector} from '@ngrx/store';
import {combineLoadingStatuses, getStoreSelector} from '@shared/helpers/store/selectors.helper';

/** Auxiliary 'createRightsSelector' variable.' */
const createRightsSelector = getStoreSelector('rights');

/**
 * Create a selector for the Are Filter Ready of the MasterContentState.
 */
const getAreFilterReady = createSelector(
	createSelector(createRightsSelector('mediaTypesLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('territoriesLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('languagesLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('brandsLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('projectTypesLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('productOwnersLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('workflowStatusesLoadingStatus'), combineLoadingStatuses),
	(mediaType, territory, language, contentCode, projectCode, productCode, workflowStatus) =>
		mediaType.loaded &&
		territory.loaded &&
		language.loaded &&
		contentCode.loaded &&
		productCode.loaded &&
		projectCode.loaded &&
		workflowStatus.loaded
);

/**
 * Create a selector for the Are Filters Failed of the MasterContentState.
 */
const getAreFiltersFailed = createSelector(
	createSelector(createRightsSelector('mediaTypesLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('territoriesLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('languagesLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('brandsLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('projectTypesLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('productOwnersLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('workflowStatusesLoadingStatus'), combineLoadingStatuses),
	createSelector(createRightsSelector('channelsLoadingStatus'), combineLoadingStatuses),
	(mediaType, territory, language, contentCode, projectCode, productCode, workflowStatus, channels) =>
		combineLoadingStatuses(
			mediaType,
			territory,
			language,
			contentCode,
			projectCode,
			productCode,
			workflowStatus,
			channels
		).error
);

/**
 * Auxiliary constant declaration with selectors for Rights page.
 */
export const rightsSelectors = {
	rightsSources: {
		data: createRightsSelector('rightsSources'),
		loadingStatus: createRightsSelector('rightsSourcesLoadingStatus'),
		selected: createRightsSelector('selectedSource'),
		view: createRightsSelector('rightsView'),
	},
	inventory: {
		data: createRightsSelector('inventory'),
		loadingStatus: createSelector(createRightsSelector('inventoryLoadingStatus'), combineLoadingStatuses),
		xlsLoadingStatus: createSelector(createRightsSelector('inventoryXLSLoadingStatus'), combineLoadingStatuses),
		grouping: createRightsSelector('inventoryGrouping'),
		pinned: createRightsSelector('inventoryPinned'),
		filters: createRightsSelector('inventoryFilters'),
		view: createRightsSelector('inventoryView'),
		notFound: createRightsSelector('inventoryNotFound'),
		xlsSelection: createRightsSelector('inventoryXLSSelection'),
	},
	mediaTypes: {
		data: createRightsSelector('mediaTypes'),
		loadingStatus: createSelector(createRightsSelector('mediaTypesLoadingStatus'), combineLoadingStatuses),
	},
	territories: {
		data: createRightsSelector('territories'),
		loadingStatus: createSelector(createRightsSelector('territoriesLoadingStatus'), combineLoadingStatuses),
	},
	languages: {
		data: createRightsSelector('languages'),
		loadingStatus: createSelector(createRightsSelector('languagesLoadingStatus'), combineLoadingStatuses),
	},
	channels: {
		data: createRightsSelector('channels'),
		loadingStatus: createSelector(createRightsSelector('channelsLoadingStatus'), combineLoadingStatuses),
	},
	genres: {
		data: createRightsSelector('genres'),
		loadingStatus: createSelector(createRightsSelector('genresLoadingStatus'), combineLoadingStatuses),
	},
	languagesMetadata: {
		data: createRightsSelector('languagesMetadata'),
		loadingStatus: createSelector(createRightsSelector('languagesMetadataLoadingStatus'), combineLoadingStatuses),
	},
	brands: {
		data: createRightsSelector('brands'),
		loadingStatus: createSelector(createRightsSelector('brandsLoadingStatus'), combineLoadingStatuses),
	},
	projectTypes: {
		data: createRightsSelector('projectTypes'),
		loadingStatus: createSelector(createRightsSelector('projectTypesLoadingStatus'), combineLoadingStatuses),
	},
	productOwners: {
		data: createRightsSelector('productOwners'),
		loadingStatus: createSelector(createRightsSelector('productOwnersLoadingStatus'), combineLoadingStatuses),
	},
	workflowStatuses: {
		data: createRightsSelector('workflowStatuses'),
		loadingStatus: createSelector(createRightsSelector('workflowStatusesLoadingStatus'), combineLoadingStatuses),
	},
	pagination: {
		currentPage: createRightsSelector('selectedPage'),
		pagesCount: createRightsSelector('pagesCount'),
		pagesCountLoadingStatus: createSelector(
			createRightsSelector('pagesCountLoadingStatus'),
			combineLoadingStatuses
		),
	},
	areFiltersReady: getAreFilterReady,
	isFiltersFailed: getAreFiltersFailed,
};
